import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../api/customFetch.js';
import mammoth from 'mammoth';

// נהלי עבודה - עיון חופשי בנהלים המשויכים לסקטור (ללא דיווח קריאה)
export default function ProceduresPage() {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    loadProcedures();
  }, []);

  const loadProcedures = async () => {
    try {
      setLoading(true);
      const { data } = await customFetch.get('/procedures/my');
      setProcedures(data.procedures || []);
    } catch {
      setError('שגיאה בטעינת הנהלים');
    } finally {
      setLoading(false);
    }
  };

  const loadDocx = useCallback(async (fileUrl) => {
    setDocxLoading(true);
    try {
      const res = await fetch(fileUrl);
      const buf = await res.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: buf });
      setDocxHtml(result.value);
    } catch {
      setDocxHtml('<p style="color:#888;text-align:center">לא ניתן להציג את המסמך – ניתן להורידו ולפתוח בוורד</p>');
    } finally {
      setDocxLoading(false);
    }
  }, []);

  const openProcedure = async (proc) => {
    setDocxHtml(null);
    try {
      const { data } = await customFetch.get(`/procedures/${proc._id}/content`);
      setSelected(data.procedure);

      if (data.procedure.fileType === 'word' && data.procedure.fileUrl) {
        loadDocx(data.procedure.fileUrl);
      }
    } catch {
      setError('שגיאה בטעינת תוכן הנוהל');
    }
  };

  const closeProcedure = () => {
    setSelected(null);
    setDocxHtml(null);
  };

  const filtered = procedures.filter((p) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      p.title.toLowerCase().includes(term) ||
      p.description?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCenter}>
          <div style={styles.spinner}></div>
          <span>טוען נהלים...</span>
        </div>
      </div>
    );
  }

  // תצוגת נוהל
  if (selected) {
    return (
      <div style={styles.page}>
        <div style={styles.modalOverlay} onClick={closeProcedure}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>📑 {selected.title}</h2>
              <button style={styles.closeBtn} onClick={closeProcedure}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {selected.description && (
                <p style={styles.description}>{selected.description}</p>
              )}

              {selected.fileUrl && (() => {
                const absoluteUrl = selected.fileUrl.startsWith('http')
                  ? selected.fileUrl
                  : `${window.location.origin}${selected.fileUrl}`;

                if (selected.fileType === 'pdf') {
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={styles.fileBar}>
                        <span>📕 {selected.fileName}</span>
                        <a href={absoluteUrl} download style={styles.downloadLink}>⬇️ הורד</a>
                      </div>
                      <iframe
                        src={absoluteUrl}
                        title={selected.fileName}
                        style={styles.fileViewer}
                      />
                    </div>
                  );
                }

                // Word - mammoth in-browser conversion
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={styles.fileBar}>
                      <span>📘 {selected.fileName}</span>
                      <a href={absoluteUrl} download style={styles.downloadLink}>⬇️ הורד</a>
                    </div>
                    <div style={styles.docxViewer}>
                      {docxLoading ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                          <div style={styles.spinner}></div>
                          <p>טוען מסמך...</p>
                        </div>
                      ) : docxHtml ? (
                        <div
                          style={styles.docxContent}
                          dangerouslySetInnerHTML={{ __html: docxHtml }}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })()}

              {selected.textContent && (
                <div style={styles.textContent}>
                  {selected.textContent.split('\n').map((line, i) =>
                    line.trim() ? (
                      <p key={i} style={{ margin: '0 0 12px' }}>{line}</p>
                    ) : (
                      <br key={i} />
                    )
                  )}
                </div>
              )}

              {!selected.textContent && !selected.fileUrl && (
                <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>
                  אין תוכן מוצג לנוהל זה
                </p>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.closeFooterBtn} onClick={closeProcedure}>
                ← חזור לרשימת הנהלים
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => nav('/user/dashboard')}>
          ← חזור לדשבורד
        </button>
        <h1 style={styles.title}>📑 נהלי עבודה</h1>
        {procedures.length > 0 && (
          <div style={styles.countBadge}>{procedures.length} נהלים</div>
        )}
      </div>

      {error && (
        <div style={styles.errorBox}>
          ⚠️ {error}
          <button onClick={() => setError(null)} style={styles.closeError}>✕</button>
        </div>
      )}

      {procedures.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📑</div>
          <h3>אין נהלי עבודה המשויכים אליך</h3>
        </div>
      ) : (
        <>
          <div style={styles.searchWrap}>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="🔍 חפש נוהל לפי שם או תיאור..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {filtered.length === 0 ? (
            <div style={styles.emptyState}>
              <h3>לא נמצאו נהלים התואמים לחיפוש</h3>
            </div>
          ) : (
            <div style={styles.grid}>
              {filtered.map((proc) => (
                <div
                  key={proc._id}
                  style={styles.procCard}
                  onClick={() => openProcedure(proc)}
                >
                  <div style={styles.procCardIcon}>
                    {proc.fileType === 'pdf' ? '📕' : proc.fileType === 'word' ? '📘' : '📑'}
                  </div>
                  <div style={styles.procCardContent}>
                    <h3 style={styles.procTitle}>{proc.title}</h3>
                    {proc.description && (
                      <p style={styles.procDesc}>{proc.description}</p>
                    )}
                    <div style={styles.procMeta}>
                      {proc.fileUrl && <span>📎 קובץ מצורף</span>}
                      {proc.hasTextContent && <span>📄 תוכן טקסטואלי</span>}
                      <span>עודכן: {new Date(proc.updatedAt || proc.createdAt).toLocaleDateString('he-IL')}</span>
                    </div>
                  </div>
                  <div style={styles.openStatus}>לעיון ←</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0cc0df 0%, #077b94 50%, #034159 100%)',
    padding: '24px 16px',
    direction: 'rtl',
  },
  loadingCenter: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '60vh', gap: 16, color: '#fff',
  },
  spinner: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff', animation: 'spin 0.8s linear infinite',
  },
  header: {
    maxWidth: 900, margin: '0 auto 24px', display: 'flex',
    alignItems: 'center', gap: 16, flexWrap: 'wrap',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff', padding: '8px 16px', borderRadius: 10, cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 600,
  },
  title: { color: '#fff', margin: 0, fontSize: '1.6rem', fontWeight: 700 },
  countBadge: {
    background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '4px 12px',
    borderRadius: 20, fontSize: '0.85rem', fontWeight: 600,
  },
  errorBox: {
    maxWidth: 900, margin: '0 auto 16px', background: '#fee2e2', color: '#991b1b',
    padding: '12px 16px', borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center',
  },
  closeError: { marginRight: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 },
  emptyState: {
    maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 16,
    padding: 60, textAlign: 'center', color: '#555',
  },
  searchWrap: { maxWidth: 900, margin: '0 auto 16px' },
  searchInput: {
    width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: 12,
    border: 'none', fontSize: '1rem', direction: 'rtl',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)', outline: 'none',
  },
  grid: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 },
  procCard: {
    background: '#fff', borderRadius: 14, padding: '16px 20px',
    display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)', border: '2px solid #3b82f6',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  procCardIcon: { fontSize: '2rem', flexShrink: 0 },
  procCardContent: { flex: 1 },
  procTitle: { margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#111' },
  procDesc: { margin: '0 0 8px', fontSize: '0.85rem', color: '#6b7280' },
  procMeta: { display: 'flex', gap: 12, fontSize: '0.8rem', color: '#9ca3af', flexWrap: 'wrap' },
  openStatus: {
    flexShrink: 0, background: '#3b82f6', color: '#fff', padding: '6px 12px',
    borderRadius: 20, fontSize: '0.85rem', fontWeight: 600,
  },

  // Modal
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 900,
    maxHeight: '95vh', display: 'flex', flexDirection: 'column', direction: 'rtl',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  modalHeader: {
    padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  modalTitle: { margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#111' },
  closeBtn: {
    background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32,
    cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modalBody: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  description: { color: '#6b7280', marginBottom: 16, fontSize: '0.95rem' },
  fileBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px', background: '#f1f5f9', borderRadius: '8px 8px 0 0',
    fontSize: '0.85rem', color: '#374151', fontWeight: 600,
    border: '1px solid #e2e8f0', borderBottom: 'none',
  },
  downloadLink: { color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 600 },
  fileViewer: {
    width: '100%', height: 520, border: '1px solid #e2e8f0',
    borderRadius: '0 0 8px 8px', display: 'block',
  },
  docxViewer: {
    border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px',
    minHeight: 200, background: '#fff',
  },
  docxContent: {
    padding: '24px 32px', lineHeight: 1.8, color: '#1f2937',
    fontSize: '0.95rem', direction: 'rtl', textAlign: 'right',
  },
  textContent: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '20px 24px', lineHeight: 1.8, color: '#1f2937',
    fontSize: '0.95rem', whiteSpace: 'pre-wrap',
  },
  modalFooter: {
    padding: '16px 24px', borderTop: '1px solid #e5e7eb',
    display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end',
  },
  closeFooterBtn: {
    background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10,
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600,
  },
};
